// Copyright (c) Docugami, Inc. All rights reserved.
import { useState, useMemo, useCallback } from 'react';
import { aaMissingClauseColor, aaVariantClauseColor, aaReviewClauseColor, homeGray } from '../shell/presentation/AppStyles';
import { dgIconNames, useDgIcons } from '../../hooks/dgIcons';
import { Fragment } from '../../networking/api';
import { ClauseCardType, useAaCardClauses } from '../../hooks/aaCardClauses';
import { decodeLabel } from 'utils/TagStandardUtils';
import { Cardlet, CardletHeaderMarkerContent, ICardletItem } from './ClauseCardletComponents';
import { ICommandButton } from 'components/cards/CommandButton';
import { ClauseImportance } from './ClauseSuggestionsList';

export const clauseCardTypeColorLookup: { [current: number]: string } = {
    [ClauseCardType.MissingClause]: aaMissingClauseColor,
    [ClauseCardType.VariantClause]: aaVariantClauseColor,
    [ClauseCardType.ReviewClause]: aaReviewClauseColor,
}

export interface AaCardProps {
    id: string;
    cardType: ClauseCardType;
    header: string;
    text: string;
    activeView: boolean;
    actionCallback: () => void;
    commands?: ICommandButton[];
    probability: number;
    fragmentProbability: number;
    selectedCallback: (id: string, fragment?: Fragment) => void;
    fragments?: Fragment[];
    importance: ClauseImportance;
    hidden?: boolean;
}

export const AaCard = (props: AaCardProps): JSX.Element => {
    const [selectedFragmentIdx, setSelectedFragmentIdx] = useState(-1);
    const { icon } = useDgIcons();
    const { fragments, selectedCallback, id } = props;

    const { expandCard } = useAaCardClauses({
        id: props.id,
        header: decodeLabel(props.header),
        text: props.text,
        cardType: props.cardType,
        fragments: props.fragments
    });

    function onExpandCard() {
        expandCard(
            selectedFragmentIdx,
            (idx: number) => select(idx),
            props.actionCallback);
    }

    const select = useCallback((idx: number) => {
        setSelectedFragmentIdx(idx);
        const fragment = idx !== -1 ? fragments?.[idx] : undefined;
        selectedCallback(id, fragment);
    }, [fragments, selectedCallback, id]);

    const clauseSuggestionExplanation = useMemo(() => {
        switch (props.cardType) {
            case ClauseCardType.MissingClause:
                if (props.probability >= 0.75) {
                    return "This clause is very common among documents in this doc set."
                }

                if (props.probability >= 0.5) {
                    return "This clause is quite common among documents in this doc set."
                }

                if (props.probability < .1) {
                    return "This clause is rarely used, but we thought it might be useful."
                }

                return "This clause is not very common among documents in this doc set, but we thought it might be useful."

            case ClauseCardType.ReviewClause:
                if (props.probability === 0) {
                    return "This clause has never been used in documents in this doc set."
                }

                if (props.probability < 0.1) {
                    return "This clause has rarely been used in documents in this doc set."
                }

                return "This clause is not often used in documents in this doc set."


            case ClauseCardType.VariantClause:
                if (props.fragments?.find(x => x.likelihood > props.fragmentProbability) !== null) {
                    return "This clause is used more often than the one in this document."
                }

                return "This clause is a variation found in this doc set."
        }
    }, [props.probability, props.cardType, props.fragments, props.fragmentProbability])

    const items: ICardletItem[] = useMemo(() => {
        const displayItem: ICardletItem = ({
            text: props.text,
            selected: selectedFragmentIdx === -1 && props.activeView,
            select: () => select(-1),
            infoText: clauseSuggestionExplanation
        });

        const variants: ICardletItem[] = props.fragments?.map((f, idx) => ({
            text: f.rawText as string,
            selected: selectedFragmentIdx === idx && props.activeView,
            select: () => select(idx)
        })) ?? [];

        return [displayItem, ...variants]
    }, [selectedFragmentIdx, props.activeView, props.text, props.fragments, select, clauseSuggestionExplanation])

    function getImportanceMarker() {
        if (props.importance === ClauseImportance.VeryImportant) {
            return (
                <CardletHeaderMarkerContent
                    label='VALUABLE'
                    icon={icon(dgIconNames.DgLightbulb, 'lightbulb_icon', 8, 10, homeGray, '')}
                    tooltip={clauseSuggestionExplanation} />
            )
        }
    }

    return (
        <Cardlet
            id={props.id}
            key={`clause_${props.id}`}
            label={decodeLabel(props.header)}
            items={items}
            isSelected={props.activeView}
            onSelect={() => !props.activeView ? select(-1) : {}}
            commands={props.commands || []}
            selectedColor={clauseCardTypeColorLookup[props.cardType]}
            onExpand={!props.hidden ? onExpandCard : undefined}
            infoHeader={getImportanceMarker()} />
    )
}
